import {
  EVENT_CATEGORY_LABELS,
  COUNTING_MODE_LABELS,
  } from '@affily/shared/constants';
import { calculateCommission } from '@affily/shared/commission';
import { formatCents, formatCount, formatPercent } from '@affily/shared/money';
import { Badge, Unknown } from './ui.jsx';

/**
 * The public actions table.
 *
 * A program that pays $2 a signup and 20% on a purchase cannot be described by
 * a single rate line, so the rate line becomes a table. Everything in it is
 * public, including the actions that pay nothing — those are what let a creator
 * see where a funnel leaks.
 */
export function EventsTable({ program, className = '' }) {
  const events = program.events ?? [];
  if (events.length <= 1) return null; // A single primary action IS the rate line.

  return (
    <section className={className} aria-labelledby="actions-heading">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h2
          id="actions-heading"
          className="text-2xs font-semibold uppercase tracking-wider text-ink-faint"
        >
          What this program pays for
        </h2>
        <p className="text-xs text-ink-faint">
          Every action the brand tracks, including the ones that don’t pay.
        </p>
      </div>

      <div className="overflow-x-auto rounded-lg border border-rule bg-paper-raised">
        <table className="data-table">
          <thead>
            <tr>
              <th>Action</th>
              <th>Pays</th>
              <th>Counts</th>
              <th className="num">
                On a {formatCents(program.averageOrderValueCents ?? 5000, { currency: program.currency })} order
              </th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.key} className={e.isPayable ? '' : 'text-ink-muted'}>
                <td>
                  <span className="font-medium text-ink">{e.name}</span>
                  {e.isPrimary && (
                    <Badge tone="accent" className="ml-2">
                      Headline
                    </Badge>
                  )}
                  <span className="mt-0.5 block text-xs text-ink-faint">
                    {EVENT_CATEGORY_LABELS[e.category] ?? e.category}
                    {e.description ? ` · ${e.description}` : ''}
                  </span>
                  {e.manualOnly && (
                    <span className="mt-0.5 block text-xs text-ink-muted">
                      Recorded by the brand when it happens, from the tracked action that led to it
                    </span>
                  )}
                </td>
                <td>
                  {e.isPayable ? (
                    <span className="font-semibold tabular-nums text-accent">{e.summary}</span>
                  ) : (
                    <span className="text-ink-faint">Tracked only — no commission</span>
                  )}
                </td>
                <td className="text-sm">
                  {COUNTING_MODE_LABELS[e.countingMode] ?? e.countingMode}
                </td>
                <td className="num">
                  <EventValue event={e} program={program} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-2 text-xs text-ink-faint">
        These are the commissions the brand pays. Affily’s fee is shown in your account, and your net
        on every commission line.
      </p>
    </section>
  );
}

/** What one action is worth per trigger, on the brand's own stated order value. */
function EventValue({ event, program }) {
  if (!event.isPayable) return <Unknown reason="This action doesn’t pay a commission" />;

  const orderAmountCents = program.averageOrderValueCents ?? 5000;
  const terms = {
    commissionType: event.commissionType ?? program.commissionType,
    rateBps: event.rateBps ?? program.rateBps,
    flatAmountCents: event.flatAmountCents ?? program.flatAmountCents,
    cookieWindowDays: program.cookieWindowDays,
    recurringMode: event.recurringMode ?? 'one_time',
    recurringMonths: event.recurringMonths ?? null,
  };

  let breakdown;
  try {
    breakdown = calculateCommission(terms, { orderAmountCents });
  } catch {
    return <Unknown reason="Terms incomplete" />;
  }

  const cycles = terms.recurringMode === 'fixed_period' ? terms.recurringMonths : 1;

  return (
    <>
      <span className="font-medium">{formatCents(breakdown.grossAmountCents, { currency: program.currency })}</span>
      {cycles > 1 && (
        <span className="block text-xs text-ink-faint">
          {formatCents(breakdown.grossAmountCents * cycles, { currency: program.currency })} over {cycles} cycles
        </span>
      )}
    </>
  );
}

/**
 * The funnel a partnership actually produced.
 *
 * Deliberately shows the drop at every step. A creator who sent 500 clicks and
 * got 40 signups but 3 purchases learns something a bare conversion count
 * cannot tell them — and so does the brand.
 */
export function EventFunnel({ funnel, uniqueClicks, className = '' }) {
  if (!funnel?.length) return null;

  const steps = [{ key: '__clicks', name: 'Unique clicks', count: uniqueClicks }, ...funnel];

  return (
    <div className={`overflow-x-auto rounded-lg border border-rule bg-paper-raised ${className}`}>
      <table className="data-table">
        <thead>
          <tr>
            <th>Step</th>
            <th className="num">Count</th>
            <th className="num">From clicks</th>
            <th className="num">Earned</th>
          </tr>
        </thead>
        <tbody>
          {steps.map((step) => {
            const rate = uniqueClicks > 0 ? (step.count / uniqueClicks) * 100 : null;
            return (
              <tr key={step.key}>
                <td className="font-medium">{step.name}</td>
                <td className="num">{formatCount(step.count)}</td>
                <td className="num">
                  {step.key === '__clicks' ? (
                    '—'
                  ) : rate === null ? (
                    <Unknown reason="No clicks yet" />
                  ) : (
                    formatPercent(rate)
                  )}
                </td>
                <td className="num">
                  {step.key === '__clicks' ? (
                    '—'
                  ) : step.isPayable ? (
                    formatCents(step.grossCents ?? 0)
                  ) : (
                    <span className="text-ink-faint">tracked only</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
